import { ChangeEvent, useCallback, useMemo } from 'react';
import { t, Trans } from '@lingui/macro';

import Plus from '@decisiv/iconix/lib/components/Plus';
import Search from '@decisiv/iconix/lib/components/Search';

import Loading from '@decisiv/ui-components/lib/components/Loading';
import Button from '@decisiv/ui-components/lib/components/Button';
import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import { useCurrentUserContext } from 'utils/User';
import useFleetAdminPermissions from 'utils/User/useFleetAdminPermissions';
import * as ManagementLayout from 'components/ManagementLayout';
import FleetAdminAction from 'components/FleetAdminAction';
import PaginationFooter from 'components/PaginationFooter';
import EmptyResults from 'components/EmptyResults';
import SearchableFilter from 'components/SearchableFilter';
import useCreateDepotVisibility from 'hooks/useModalVisibility';
import { SearchProvider } from 'contexts/SearchContext';
import { NoWrapHeaderTable } from '../components/styles';
import { useManagedDepotsQuery } from './hooks/useManagedDepotsQuery';

import useOemSegmentsFilters from '../OemSegments/hooks/useOemSegmentsFilters';
import useTeamsFilters from '../Teams/hooks/useTeamsFilters';
import useAccountsFilters from '../Fleets/hooks/useAccountsFilters';
import { useManagementPageContext } from '../contexts/ManagementPageContext';
import useDepotsFilters from './hooks/useDepotsFilters';
import { generateColumns } from './columns';

import CreateDepotModal from './CreateDepotModal';
import { ClearAllButton } from '../styles';
import useTableUrlState from '../hooks/useTableUrlState';

export type ManagedDepotsType = ExtractFromArray<
  ManagedDepotsQuery['managedAssetGroupings']['collection']
>;

const Depots = () => {
  const fleetAdminPermissions = useFleetAdminPermissions();
  const createDepotCapability =
    fleetAdminPermissions.anyAccount('canManageDepots');

  const {
    isFiltersDirty,
    pageParams,
    handleChangeOemSegmentsFilter,
    handleChangeTeamsFilter,
    handleChangeAccountsFilter,
    handleChangeDepotsFilter,
    handleClearFilters,
  } = useManagementPageContext();

  const {
    visible: createDepotModalVisible,
    openModal: openCreateDepotModal,
    closeModal: closeCreateDepotModal,
  } = useCreateDepotVisibility();

  const {
    page,
    setPage,
    apiSort,
    defaultSort,
    onSort,
    liveSearch,
    setLiveSearch,
    debouncedSearch,
  } = useTableUrlState<CustomersSortColumn>('management-depots');

  const { oemSegments } = useCurrentUserContext();
  const amountOfOemSegmentsManaged = oemSegments?.length ?? 0;

  const handleOnTextChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setLiveSearch(e.target.value);
    },
    [setLiveSearch],
  );

  const { data, loading } = useManagedDepotsQuery({
    variables: {
      sort: apiSort,
      page,
      ids: pageParams.depotIds,
      platformAccountIds: pageParams.accountIds,
      sentinelTeamIds: pageParams.teamIds,
      oemSegmentsIds: pageParams.oemSegmentIds,
      text: debouncedSearch.length > 2 ? debouncedSearch : undefined,
    },
  });

  const oemSegmentsFilterProps = useOemSegmentsFilters({
    value: pageParams.oemSegmentIds,
    onSubmit: handleChangeOemSegmentsFilter,
  });

  const teamFilterProps = useTeamsFilters({
    value: pageParams.teamIds,
    oemSegmentIds: pageParams.oemSegmentIds,
    onSubmit: handleChangeTeamsFilter,
  });

  const accountsFilters = useAccountsFilters({
    value: pageParams.accountIds,
    oemSegmentsIds: pageParams.oemSegmentIds,
    sentinelTeamsIds: pageParams.teamIds,
    onSubmit: handleChangeAccountsFilter,
  });

  const depotsFilters = useDepotsFilters({
    value: pageParams.depotIds,
    oemSegmentsIds: pageParams.oemSegmentIds,
    sentinelTeamsIds: pageParams.teamIds,
    platformAccountIds: pageParams.accountIds,
    onSubmit: handleChangeDepotsFilter,
  });

  const columns = useMemo(
    () => generateColumns({ onSort, amountOfOemSegmentsManaged }),
    [onSort, amountOfOemSegmentsManaged],
  );

  const tableData = data?.managedAssetGroupings.collection ?? [];
  const pagination = data?.managedAssetGroupings.pagination;

  return (
    <ManagementLayout.TabBody.Container>
      <ManagementLayout.TabBody.Header.Container>
        <ManagementLayout.TabBody.Header.Left>
          <FleetAdminAction capability={createDepotCapability}>
            {({ disabled }) => (
              <Button
                text={t`Create Depot`}
                icon={Plus}
                size="small"
                disabled={disabled}
                onClick={
                  /* istanbul ignore next -- @preserve */ () =>
                    openCreateDepotModal()
                }
              />
            )}
          </FleetAdminAction>
        </ManagementLayout.TabBody.Header.Left>
        <ManagementLayout.TabBody.Header.Right>
          <P size="small" marginRight={1}>
            <Trans>Search Depots by Name:</Trans>
          </P>
          <SearchInput
            value={liveSearch}
            onChange={handleOnTextChange}
            hideLabel
            name="searchByDepot"
            size="small"
          />
        </ManagementLayout.TabBody.Header.Right>
      </ManagementLayout.TabBody.Header.Container>
      <ManagementLayout.TabBody.Content.Container>
        <ManagementLayout.TabBody.Content.Filters>
          {amountOfOemSegmentsManaged > 1 && (
            <SearchableFilter
              popoverLabel={t`OEM Segments`}
              placement="bottom-start"
              {...oemSegmentsFilterProps}
            />
          )}
          <SearchableFilter popoverLabel={t`Teams`} {...teamFilterProps} />
          <SearchableFilter popoverLabel={t`Accounts`} {...accountsFilters} />
          <SearchableFilter popoverLabel={t`Depots`} {...depotsFilters} />
          {isFiltersDirty && (
            <ClearAllButton
              variant="ghost"
              onClick={handleClearFilters}
              text={t`Clear All`}
              size="small"
            />
          )}
        </ManagementLayout.TabBody.Content.Filters>
        {loading && (
          <Loading
            flex={1}
            justifyContent="center"
            size="large"
            title={t`Loading...`}
          />
        )}
        {!loading && tableData.length === 0 && (
          <EmptyResults
            title={t`No Depots Found`}
            subtitle={t`No Managed Depots Found`}
            icon={Search}
          />
        )}
        {!loading && tableData.length > 0 && (
          <SearchProvider searchString={debouncedSearch}>
            <NoWrapHeaderTable
              height="100%"
              scroll={['x', 'y']}
              data={tableData}
              columns={columns}
              onSort={onSort}
              defaultSort={defaultSort}
              footer={() => (
                <PaginationFooter
                  // @ts-expect-error - JS file
                  title={t`Depots`}
                  setCurrentPage={setPage}
                  totalOnPage={tableData.length}
                  page={pagination?.page}
                  perPage={pagination?.perPage}
                  totalEntries={pagination?.totalEntries}
                  totalPages={pagination?.totalPages}
                />
              )}
            />
          </SearchProvider>
        )}
      </ManagementLayout.TabBody.Content.Container>
      {createDepotModalVisible && (
        /* istanbul ignore next -- @preserve */
        <CreateDepotModal
          visible={createDepotModalVisible}
          handleClose={closeCreateDepotModal}
        />
      )}
    </ManagementLayout.TabBody.Container>
  );
};

export default Depots;
